import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaCopy, FaTimes, FaExpand, FaCompress, FaPlus, FaFile } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import '../styles/BaseContentForm.css';

/**
 * BaseContentForm Component
 * A reusable form component for content management with slide-in functionality
 * 
 * @component
 */
function BaseContentForm({ 
  isOpen, 
  onClose, 
  onSubmit,
  contentTypes,
  savedContents,
  initialContent,
  title,
  submitButtonText,
  extraActions
}) {

  // State management
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [formContent, setFormContent] = useState(initialContent);
  const [contextSearch, setContextSearch] = useState('');
  const [showContextSearch, setShowContextSearch] = useState(false);
  const [filteredContents, setFilteredContents] = useState([]);
  const textAreaRef = useRef(null);
  const searchInputRef = useRef(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [hoveredChipId, setHoveredChipId] = useState(null);
  const previewLayerRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Add maxTitleLength constant at the top of the component
  const MAX_TITLE_LENGTH = 140;

  /**
   * Updates a single field in the form content
   * @param {string} field - The field to update
   * @param {string} value - The new value
   */
  const handleFieldChange = (field, value) => {
    if (field === 'title' && value.length > MAX_TITLE_LENGTH) {
      toast.error(`Title must be ${MAX_TITLE_LENGTH} characters or less`);
      return;
    }
    
    // Special handling for content_type to ensure it's an integer
    if (field === 'content_type') {
      const parsedValue = value ? parseInt(value, 10) : '';
  
      
      setFormContent(prev => ({
        ...prev,
        [field]: parsedValue
      }));
    } else {
      setFormContent(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  /**
   * Handles form submission with validation
   * @param {Event} e - Form submission event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      if (!formContent.title?.trim()) {
        throw new Error('Title is required');
      }
      if (!formContent.content?.trim()) {
        throw new Error('Content is required');
      }

      // Extract references from content
      const references = [];
      const content = formContent.content;
      const contextPattern = /~~\[@(\d+)\].*?~~/g;
      let match;
      let position = 0;
      
      while ((match = contextPattern.exec(content)) !== null) {
        const id = parseInt(match[1], 10);
        references.push({ id, position: position++ });
      }

      // Add references to form data
      const submissionData = {
        ...formContent,
        contextReferences: references
      };
      
      onSubmit(submissionData);
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Handles context search and filters saved contents
   * @param {Event} e - Input change event
   */
  const handleContextSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setContextSearch(e.target.value);
    
    if (searchTerm) {
      const filtered = savedContents.filter(content => 
        content.title.toLowerCase().includes(searchTerm)
      );
      setFilteredContents(filtered);
    } else {
      setFilteredContents(savedContents || []);
    }
  };

  /**
   * Handles keyboard navigation in search results
   * @param {Event} e - Keyboard event
   */
  const handleSearchKeyDown = (e) => {
    if (!showContextSearch) return;

    const items = filteredContents.length > 0 ? filteredContents : (savedContents || []);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < items.length) {
          insertContextAtCursor(items[selectedIndex]);
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowContextSearch(false);
        setSelectedIndex(-1);
        textAreaRef.current?.focus();
        break;
      default:
        break;
    }
  };

  /**
   * Checks if a content has references
   * @param {Object} content - The content to check
   * @returns {boolean} - Whether the content has references
   */
  const hasReferences = (content) => {
    return content.context_references && content.context_references.length > 0;
  };

  /**
   * Adds selected content as a reference
   * @param {Object} selectedContent - The content to add as reference
   */
  const insertContextAtCursor = (selectedContent) => {
    try {
      if (!textAreaRef.current) return;

      // Check if the selected content has references
      if (hasReferences(selectedContent)) {
        toast.error(
          <div>
            Cannot add reference to "{selectedContent.title}" because it contains references to:
            <ul style={{ marginTop: '8px', marginBottom: '0' }}>
              {selectedContent.context_references.map(ref => (
                <li key={ref.id}>{ref.title}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 5000 }
        );
        return;
      }

      const textarea = textAreaRef.current;
      const cursorPosition = textarea.selectionStart;
      const currentContent = formContent.content || '';
      
      // Only add newline before if there's content and we're not at the start
      const needsNewlineBefore = currentContent.length > 0;
      const contextMarker = `${needsNewlineBefore ? '\n' : ''} ~~[@${selectedContent.id}]${selectedContent.title}~~ \n`;
      
      const newContent = 
        currentContent.slice(0, cursorPosition) + 
        contextMarker +
        currentContent.slice(cursorPosition);

      setFormContent(prev => ({
        ...prev,
        content: newContent
      }));

      // Reset search state
      setContextSearch('');
      setFilteredContents([]);
      setShowContextSearch(false);
      
      // Set cursor position after the context marker and the newline
      requestAnimationFrame(() => {
        textarea.focus();
        const newPosition = cursorPosition + contextMarker.length;
        textarea.setSelectionRange(newPosition, newPosition);
      });

      toast.success('Context reference added successfully!');
    } catch (error) {
      toast.error('Failed to add context reference');
    }
  };

  /**
   * Combines main content with referenced contents for preview
   * @returns {string} Combined content
   */
  const getProcessedContent = () => {
    if (!formContent.content || !Array.isArray(savedContents)) {
      return formContent.content || '';
    }

    let processedContent = formContent.content;
    const contextPattern = /(?:^|\s)~~\[@(\d+)\].*?~~(?:\s|$)/g;

    processedContent = processedContent.replace(contextPattern, (match, contextId) => {
      const referencedContent = savedContents.find(c => c.id === parseInt(contextId, 10));
      return referencedContent ? (match.startsWith(' ') ? ' ' : '') + referencedContent.content + ' ' : match;
    });

    return processedContent;
  };

  /**
   * Copies content to clipboard with error handling
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getProcessedContent());
      toast.success('Content copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy content');
    }
  };

  // Handle @ mentions and context menu positioning
  const handleContentKeyDown = (e) => {
    if (e.key === '@') {
      const textarea = textAreaRef.current;
      const { selectionStart } = textarea;
      const bounds = textarea.getBoundingClientRect();
      
      // Calculate the position of the cursor
      const textBeforeCursor = textarea.value.substring(0, selectionStart);
      const lines = textBeforeCursor.split('\n');
      const currentLineNumber = lines.length - 1;
      
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const yPos = currentLineNumber * lineHeight;
      
      setContextMenuPosition({
        x: bounds.left,
        y: bounds.top + yPos + lineHeight
      });
      
      setShowContextSearch(true);
      setFilteredContents(savedContents || []);
      e.preventDefault();
    } else if (e.key === 'Backspace') {
      handleBackspace(e);
    }
  };

  // Add handleBackspace function
  const handleBackspace = (e) => {
    const textarea = textAreaRef.current;
    const cursorPosition = textarea.selectionStart;
    const content = formContent.content || '';
    
    // Check if cursor is at the end or within a context reference
    const beforeCursor = content.slice(0, cursorPosition);
    const contextPattern = / ~~\[@\d+\].*?~~(?:\s|$)/g;
    let match;
    let lastMatch = null;
    let lastMatchEnd = 0;
    
    while ((match = contextPattern.exec(beforeCursor)) !== null) {
      lastMatch = match;
      lastMatchEnd = match.index + match[0].length;
    }

    // If cursor is right after or within a context reference
    if (lastMatch && cursorPosition <= lastMatchEnd && cursorPosition > lastMatch.index) {
      e.preventDefault();
      const newContent = 
        content.slice(0, lastMatch.index) + 
        content.slice(lastMatchEnd);
      
      setFormContent(prev => ({
        ...prev,
        content: newContent
      }));
      
      // Set cursor position to where the context reference was
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(lastMatch.index, lastMatch.index);
      });
    }
  };

  // Focus search input when context menu opens
  useEffect(() => {
    if (showContextSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showContextSearch]);

  // Initialize form content
  useEffect(() => {
    if (initialContent) {
      // Properly handle content_type to ensure it's always a number
      let contentTypeId;
      
      if (typeof initialContent.content_type === 'object' && initialContent.content_type !== null) {
        contentTypeId = initialContent.content_type.id;
      } else if (typeof initialContent.content_type === 'number') {
        contentTypeId = initialContent.content_type;
      } else if (typeof initialContent.content_type === 'string' && !isNaN(parseInt(initialContent.content_type, 10))) {
        contentTypeId = parseInt(initialContent.content_type, 10);
      } else {
        contentTypeId = '';
      }
      
            setFormContent({
        ...initialContent,
        content_type: contentTypeId,
        title: initialContent.title || `New Content: ${new Date().toLocaleString()}`
      });
    }
  }, [initialContent]);


  // Check if this is a Canvas document (HTML content)
  const isCanvasContent = () => {
    if (!formContent?.content_type || !contentTypes) return false;
    const contentType = typeof formContent.content_type === 'object' 
      ? formContent.content_type 
      : contentTypes.find(ct => ct.id === formContent.content_type);
    return contentType?.name === 'Canvas';
  };

  // Function to parse and render content with styled context references
  const renderStyledContent = () => {
    if (!formContent?.content) return '';

    // If this is Canvas content, render as HTML
    if (isCanvasContent()) {
      return (
        <div 
          className="canvas-content-preview"
          dangerouslySetInnerHTML={{ __html: formContent.content }}
          style={{
            fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
            fontSize: '11pt',
            lineHeight: '1.5',
            color: '#202124',
            padding: '0',
            minHeight: '100px'
          }}
        />
      );
    }

    // Regular content with context references
    const parts = [];
    let lastIndex = 0;
    const contextPattern = /(?:^|\s)~~\[@(\d+)\].*?~~(?:\s|$)/g;
    const content = formContent.content;
    
    let match;
    while ((match = contextPattern.exec(content)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.slice(lastIndex, match.index)
        });
      }

      // Find the referenced content
      const referencedContent = savedContents?.find(c => c.id === parseInt(match[1], 10));
      if (referencedContent) {
        // Add the leading space as a text part if it exists
        if (match[0].startsWith(' ')) {
          parts.push({
            type: 'text',
            content: ' '
          });
        }
        parts.push({
          type: 'context',
          id: match[1],
          title: referencedContent.title,
          fullMatch: match[0].trim()
        });
      } else {
        // If content not found, show the reference as is
        parts.push({
          type: 'text',
          content: match[0]
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex)
      });
    }

    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={index}>{part.content}</span>;
      } else {
        return (
          <span
            key={index}
            className="context-chip"
            data-raw={part.fullMatch}
            onMouseEnter={() => setHoveredChipId(part.id)}
            onMouseLeave={() => setHoveredChipId(null)}
          >
            <FaFile className="context-icon" />
            <span className="chip-content">{part.title}</span>
            {hoveredChipId === part.id && (
              <button
                className="remove-context"
                onClick={(e) => {
                  e.preventDefault();
                  removeContext(` ~~${part.fullMatch}~~ `);
                }}
              >
                <FaTimes />
              </button>
            )}
          </span>
        );
      }
    });
  };

  // Function to remove a context reference
  const removeContext = (contextMatch) => {
    const newContent = formContent.content.replace(contextMatch, '');
    setFormContent(prev => ({
      ...prev,
      content: newContent
    }));
    
    // Focus back on the textarea after removing context
    requestAnimationFrame(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    });
  };

  // Function to sync scroll position between textarea and preview
  const handleScroll = (e) => {
    if (previewLayerRef.current) {
      previewLayerRef.current.scrollTop = e.target.scrollTop;
      previewLayerRef.current.scrollLeft = e.target.scrollLeft;
    }
  };


  // UI Components
  const renderContextSearch = () => (
    <div className="context-search-wrapper">
      <input
        ref={searchInputRef}
        type="text"
        value={contextSearch}
        onChange={handleContextSearch}
        onKeyDown={handleSearchKeyDown}
        placeholder="Search saved contents..."
        className="context-search-input"
      />
      <div className="context-search-results">
        {(filteredContents.length > 0 ? filteredContents : savedContents || []).map((content, index) => (
          <div 
            key={content.id} 
            className={`context-search-item ${index === selectedIndex ? 'selected' : ''}`}
            onClick={() => insertContextAtCursor(content)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="context-title">{content.title}</span>
            <button type="button" className="add-context-item-button">
              <FaPlus />
            </button>
          </div>
        ))}
      </div>
    </div>
  );


  // Update the content area JSX
  const renderContentArea = () => (
    <div className="content-area">
      <div className="content-controls">
        <button 
          type="button"
          className="add-context-button"
          onClick={() => {
            setShowContextSearch(!showContextSearch);
            setFilteredContents(savedContents || []);
            setSelectedIndex(-1);
          }}
        >
          <FaPlus /> Add Context
        </button>
        
        {showContextSearch && (
          <div className="context-search-container">
            <input
              ref={searchInputRef}
              type="text"
              value={contextSearch}
              onChange={handleContextSearch}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search saved contents..."
              className="context-search-input"
            />
            <div className="context-search-results">
              {(filteredContents.length > 0 ? filteredContents : savedContents || []).map((content, index) => (
                <div 
                  key={content.id} 
                  className={`context-search-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => insertContextAtCursor(content)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <FaFile className="context-icon" />
                  <span className="context-title">{content.title}</span>
                  <button type="button" className="add-context-item-button">
                    <FaPlus />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="content-textarea-wrapper">
        <div 
          ref={previewLayerRef}
          className="content-preview-layer"
        >
          {renderStyledContent()}
        </div>
        {!isCanvasContent() && (
          <textarea
            ref={textAreaRef}
            id="content"
            value={formContent?.content || ''}
            onChange={(e) => handleFieldChange('content', e.target.value)}
            onKeyDown={handleContentKeyDown}
            onScroll={handleScroll}
            placeholder="Add content (type @ to add context)"
            rows={10}
            required
            style={{
              width: '100%',
              resize: 'vertical',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              caretColor: 'black',
              color: 'transparent',
              background: 'transparent'
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <div 
        className={`overlay ${isOpen ? 'open' : ''} ${isFullScreen ? 'fullscreen' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-in-form ${isOpen ? 'open' : ''} ${isFullScreen ? 'fullscreen' : ''}`}>
        <div className="form-header">
          <h2>{title}</h2>
          <div className="form-controls">
            <button 
              className="icon-button"
              onClick={() => setIsFullScreen(!isFullScreen)}
              title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
            >
              {isFullScreen ? <FaCompress /> : <FaExpand />}
            </button>
            <button className="close-button" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="form-content-wrapper">
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  id="title"
                  type="text"
                  value={formContent?.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder={`New Content: ${new Date().toLocaleString()}`}
                  className={!formContent?.title ? 'default-title' : ''}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formContent?.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Enter description"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">Content</label>
                {renderContentArea()}
              </div>

              <div className="form-group">
                <label htmlFor="content_type">Content Type</label>
                <select
                  id="content_type"
                  value={formContent?.content_type || ''}
                  onChange={(e) => {
                    handleFieldChange('content_type', e.target.value);
                  }}
                >
                  <option value="">Select a type</option>
                  {contentTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                {extraActions}
                <div className="form-actions-right">
                  <button type="submit" className="action-button">
                    {submitButtonText}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>

          {isFullScreen && (
            <div className="preview-section">
              <div className="preview-header">
                <h3>Output Preview</h3>
                <button 
                  className="icon-button"
                  onClick={handleCopy}
                  title="Copy to clipboard"
                >
                  <FaCopy />
                </button>
              </div>
              <div className="preview-content">
                {getProcessedContent() || <span className="placeholder">Your content will appear here...</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

BaseContentForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  contentTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  savedContents: PropTypes.array,
  initialContent: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
    content_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  title: PropTypes.string.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  extraActions: PropTypes.node
};

export default BaseContentForm; 